import type { MouseEvent, ReactNode } from "react";
import { LOGOUT_URL } from "../api.ts";
import logoUrl from "../assets/sprocket-logo.svg";
import { navigate, ROUTES, type RoutePath } from "../router.ts";
import { Avatar } from "./Avatar.tsx";
import { Background } from "./Background.tsx";
import {
  CorporateLinkIcon,
  GridIcon,
  HistoryIcon,
  LogoutIcon,
  ShieldIcon,
  UserIcon,
} from "./icons.tsx";

const NAV_ITEMS: { path: RoutePath; label: string; icon: ReactNode }[] = [
  { path: ROUTES.products, label: "プロダクト", icon: <GridIcon /> },
  { path: ROUTES.profile, label: "基本設定", icon: <UserIcon /> },
  { path: ROUTES.security, label: "ログインとセキュリティ", icon: <ShieldIcon /> },
  { path: ROUTES.loginHistory, label: "ログイン記録", icon: <HistoryIcon /> },
];

/** 同じタブでの通常のクリックだけを画面内の遷移にする（Cmd+クリックなどはブラウザに任せる） */
function onLinkClick(path: RoutePath) {
  return (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      return;
    }
    e.preventDefault();
    navigate(path);
  };
}

/** 画面内リンク */
export function AppLink({
  to,
  className,
  children,
}: {
  to: RoutePath;
  className?: string;
  children: ReactNode;
}) {
  return (
    <a href={to} className={className} onClick={onLinkClick(to)}>
      {children}
    </a>
  );
}

/**
 * ヘッダーと背景だけの枠（拒否画面などナビの無い画面でも使う）。
 * コーポレートサイトへのリンクとコピーライトのフッターは、プロダクト画面にだけ出す
 */
export function Shell({
  header,
  footer = false,
  children,
}: {
  header?: ReactNode;
  footer?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="shell">
      <Background />
      <header className="header">
        <a href={ROUTES.products} className="header__brand" onClick={onLinkClick(ROUTES.products)}>
          <img src={logoUrl} alt="Sprocket" width={121} height={26} />
          <span className="header__product">アカウント設定</span>
        </a>
        {header}
      </header>
      {children}
      {footer ? <Footer /> : null}
    </div>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <a
        href="https://www.sprocket.bz/"
        className="footer__link"
        target="_blank"
        rel="noopener noreferrer"
      >
        <CorporateLinkIcon />
        コーポレートサイト
      </a>
      <small className="footer__copyright">Copyright (C) Sprocket Inc. All Rights Reserved.</small>
    </footer>
  );
}

type LayoutProps = {
  path: RoutePath;
  userName: string;
  picture: string | null;
  children: ReactNode;
};

/** ログイン後の共通レイアウト（ヘッダー・サイドナビ。フッターはプロダクト画面だけ） */
export function Layout({ path, userName, picture, children }: LayoutProps) {
  return (
    <Shell
      footer={path === ROUTES.products}
      header={
        <div className="header__account">
          <div className="header__user">
            <Avatar name={userName} picture={picture} size="small" />
            <span className="header__user-name">{userName}</span>
          </div>
          <a href={LOGOUT_URL} className="button button--ghost button--small header__logout">
            <LogoutIcon size={16} />
            ログアウト
          </a>
        </div>
      }
    >
      <div className="body">
        <nav aria-label="アカウント設定" className="side-nav">
          {NAV_ITEMS.map((item) => (
            <a
              key={item.path}
              href={item.path}
              className="side-nav__item"
              aria-current={item.path === path ? "page" : undefined}
              onClick={onLinkClick(item.path)}
            >
              {item.icon}
              <span>{item.label}</span>
            </a>
          ))}
        </nav>
        <main className="main">{children}</main>
      </div>
    </Shell>
  );
}
